import { useCallback, useEffect, useMemo, useState } from "react"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { BetValuesFailure, GameBetValues } from "services/bonus/type"

import { missingCurrencies, normalizeCoins, tiersForCurrency } from "../../helpers/spin-stake.helpers"

/**
 * SOFT-736 — the bet ladders of the template's free-spins games.
 *
 * Loads one game at a time (the endpoint is deliberately per-game: the catalogue-wide sweep it
 * replaced ran for 13h40m) and keeps the three outcomes apart, because collapsing them is the
 * exact defect this ticket exists to fix:
 *
 * - `ready`         — real ladders, the picker can offer tiers;
 * - `empty`         — answered, but this game has no ladder yet: it was never synced, and the
 *                     Sync button is the fix;
 * - `unavailable`   — the ladders could NOT BE READ (503). NOT "no tiers": that is the state the
 *                     operator is trying to detect, so the picker keeps the current selection and
 *                     offers a retry instead of showing an empty list;
 * - `in_progress`   — a sync for this game is already running (409) — wait and retry, nothing is
 *                     broken, so this must not read like an outage;
 * - `not_supported` — this game's provider has no bet ladders at all (400). Permanent: syncing
 *                     and retrying can never produce tiers, so the fix is a different game;
 * - `not_found`     — the selected game is not in this tenant's catalog (404) — a wrong id, which
 *                     belongs on the games field, not on a currency row.
 */
export type BetValuesState = "idle" | "loading" | "ready" | "empty" | "unavailable" | "in_progress" | "not_supported" | "not_found"

export interface GameBetValuesCtx {
  state: BetValuesState
  /** One entry per selected game, in the order they were requested. */
  ladders: GameBetValues[]
  /** Currencies at least one selected game has no ladder for — narrowed to the TENANT's
   *  currencies (plus USD, the fallback's currency): the provider prices ~19 fiat currencies,
   *  and a chip for one the tenant never offers is only something to worry about for nothing. */
  missingFiat: string[]
  /** The tiers valid on EVERY selected game for this currency; `null` = at least one has none. */
  tiersFor: (coin: string) => number[] | null
  syncing: boolean
  /** Refetch the provider's ladders. Empty list = the tenant's fiat currencies (all of the
   *  provider's only when the tenant list is unknown). */
  sync: (currencies?: string[]) => Promise<void>
  reload: () => void
  /** The last failure reason, kept so a retry can be offered only where it can help. */
  lastError: BetValuesFailure | null
  /** What the last completed sync did. Cleared when the game selection changes, a reload is
   *  requested, or another sync starts. */
  lastSync: SyncOutcome | null
}

/**
 * The result of one sync, in the two facts an operator needs: what arrived, and what is still
 * not there. They are independent — a sync can fetch one currency and fail another — so neither
 * may suppress the other, which is how "success that changed nothing" used to look like a button
 * that does nothing.
 */
export interface SyncOutcome {
  /** Currencies the provider returned values for, union over the selected games. `null` ONLY
   *  when no backend reported the field (an older service); an explicit `[]` is the fact that
   *  the provider returned nothing, and must never be confused with the two. */
  fetched: string[] | null
  /** Of the currencies this sync covered, the ones at least one selected game still has no
   *  ladder for. Read off the returned ladders, so it holds even when `fetched` is unknown. */
  stillMissing: string[]
}

/**
 * "Ready" means EVERY selected game has ladders, not just one of them: a stake has to be a tier on
 * all of them, so one unladdered game leaves every picker empty. Reporting `ready` off the game
 * that happens to have values would show a green light above a form that cannot be filled in.
 */
/** A failure keeps its own identity: "wait", "wrong game" and "this provider never has tiers"
 *  each have a different fix, and only one of them is worth retrying blindly. */
const failureState = (reason: BetValuesFailure): BetValuesState => {
  switch (reason) {
    case "not_found":
      return "not_found"
    case "not_supported":
      return "not_supported"
    case "in_progress":
      return "in_progress"
    default:
      return "unavailable"
  }
}

const readyState = (ladders: GameBetValues[]): BetValuesState =>
  ladders.length > 0 && ladders.every((l) => Object.keys(l.bet_values ?? {}).length > 0) ? "ready" : "empty"

export const useGameBetValues = (gameIds: string[], tenant: string, tenantCurrencies: string[] = []): GameBetValuesCtx => {
  const [ladders, setLadders] = useState<GameBetValues[]>([])
  const [state, setState] = useState<BetValuesState>("idle")
  const [syncing, setSyncing] = useState(false)
  const [lastError, setLastError] = useState<BetValuesFailure | null>(null)
  const [lastSync, setLastSync] = useState<SyncOutcome | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  // The ids as a stable key: a new array with the same contents must not refetch on every render.
  const idsKey = gameIds.join(",")
  // The tenant's currencies plus USD (the fallback's currency, which always needs a ladder).
  // Empty when the tenant list failed to load or has not arrived yet — and empty means "narrow
  // nothing", because hiding real gaps behind a failed settings call would be worse than showing
  // a few extra chips. Joined only to key the memo; the array below is what the code uses.
  const relevantKey = tenantCurrencies.length ? normalizeCoins([...tenantCurrencies, "USD"]).join(",") : ""
  const relevantCoins = useMemo(() => (relevantKey ? relevantKey.split(",") : []), [relevantKey])

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : []
    if (ids.length === 0) {
      setLadders([])
      setState("idle")
      setLastError(null)
      setLastSync(null)
      return
    }
    setLastSync(null)

    let cancelled = false
    setState("loading")
    Promise.all(ids.map((id) => AdminBonus2Service.getGameBetValues(id, tenant))).then((results) => {
      if (cancelled) return
      // One failure decides the whole set: a stake must be a tier on EVERY game, so a partial
      // answer cannot be presented as the truth about what may be picked.
      const failed = results.find((r) => !r.ok)
      if (failed && !failed.ok) {
        setLadders([])
        setLastError(failed.reason)
        setState(failureState(failed.reason))
        return
      }
      const loaded = results.map((r) => (r.ok ? r.data : null)).filter((d): d is GameBetValues => d !== null)
      setLadders(loaded)
      setLastError(null)
      setState(readyState(loaded))
    })

    return () => {
      cancelled = true
    }
  }, [idsKey, tenant, reloadToken])

  const reload = useCallback(() => setReloadToken((n) => n + 1), [])

  const sync = useCallback(
    async (currencies: string[] = []) => {
      const ids = idsKey ? idsKey.split(",") : []
      if (ids.length === 0) return
      // No explicit list = the tenant's currencies, not the provider's whole roster: syncing a
      // ladder for a currency nobody here can bet in is a provider call spent on nothing. An
      // empty list still means "every fiat currency" to the backend — which is what happens
      // while the tenant's currencies are unknown.
      const requested = normalizeCoins(currencies.length > 0 ? currencies : relevantCoins)
      // What this sync can report on. When it asked for everything, that is whatever was missing
      // BEFORE — otherwise a sync with no explicit list could never name a currency at all.
      const scope = requested.length > 0 ? requested : missingCurrencies(ladders)
      setSyncing(true)
      setLastSync(null)
      // Sequential, never fanned out: each call walks the provider currency by currency, and the
      // whole point of the per-game endpoint is that nothing here multiplies.
      const results = []
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        results.push(await AdminBonus2Service.syncGameBetValues(id, tenant, requested))
      }
      setSyncing(false)

      const failed = results.find((r) => !r.ok)
      if (failed && !failed.ok) {
        setLastError(failed.reason)
        setState(failureState(failed.reason))
        return
      }
      const loaded = results.map((r) => (r.ok ? r.data : null)).filter((d): d is GameBetValues => d !== null)
      // `fetched` is a FACT only when every game reported it: an empty report from one game is
      // "the provider priced nothing for it", which must not be dropped — a sibling that did get
      // values would then present a partial sync as a complete one.
      const reports = loaded.map((d) => d.synced_currencies).filter((s): s is string[] => Array.isArray(s))
      const fetched = reports.length === loaded.length ? normalizeCoins(reports.flat()) : null
      // Read off the ladders rather than the report, so this holds even when `fetched` is
      // unknown — and it is per-game by construction: `missing_fiat` unions every selected game,
      // so a currency one game still lacks stays listed even if another game just got it.
      const missingAfter = new Set(missingCurrencies(loaded))
      setLastSync({ fetched, stillMissing: scope.filter((coin) => missingAfter.has(coin)) })
      setLadders(loaded)
      setLastError(null)
      setState(readyState(loaded))
    },
    [idsKey, tenant, relevantCoins, ladders]
  )

  const missingFiat = useMemo(() => {
    const missing = missingCurrencies(ladders)
    if (relevantCoins.length === 0) return missing
    const relevant = new Set(relevantCoins)
    return missing.filter((coin) => relevant.has(coin))
  }, [ladders, relevantCoins])
  const tiersFor = useCallback((coin: string) => tiersForCurrency(ladders, coin), [ladders])

  return { state, ladders, missingFiat, tiersFor, syncing, sync, reload, lastError, lastSync }
}
